import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { History, Loader2, MapPin, Star, Send } from 'lucide-react';
import api from '../utils/api';
import AddToInviteCartButton from './AddToInviteCartButton';
import { useInviteTalentToShift } from './InviteTalentToShiftProvider';
import { useTalentProfileModal } from './TalentProfileModalProvider';
import { formatDate } from '../utils/timeFormat';

/**
 * Iter-187 — Previous Workers card for AdminShiftDetail.
 *
 * Surfaces talent who have already worked for this client OR who have
 * completed a similar shift (same role, fuzzy match on venue name).
 * Each row gives admins an "Invite" CTA + an "Add to invite cart"
 * toggle so they can build a multi-talent invite alongside picks from
 * Discover, Favorites, etc.
 *
 * Tabs:
 *   • All — combined results, top 50 by recency.
 *   • For this client — talent the client has hired before.
 *   • Same role/venue — talent with matching role or venue history.
 */
const PreviousWorkersSection = ({ shiftId, clientId, roleId, venueName, roleName }) => {
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const { open: openInviteToShift } = useInviteTalentToShift();
  const { open: openProfile } = useTalentProfileModal();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!clientId && !roleId && !venueName) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (tab === 'client' || tab === 'all') {
          if (clientId) params.set('client_id', clientId);
        }
        if (tab === 'role' || tab === 'all') {
          if (roleId) params.set('role_id', roleId);
          if (venueName) params.set('venue_name', venueName);
        }
        // Tab "client" without a client falls through to empty.
        if (!params.toString()) {
          if (!cancelled) setRows([]);
          return;
        }
        const res = await api.get(`/admin/talent/previous-workers?${params.toString()}&limit=50`);
        if (!cancelled) setRows(res.data || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tab, clientId, roleId, venueName]);

  const TabBtn = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
        tab === id
          ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
          : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
      }`}
      data-testid={`prev-workers-tab-${id}`}
    >
      {label}
    </button>
  );

  return (
    <Card className="bg-white" data-testid="previous-workers-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4 text-indigo-600" />
            Previous Workers
            {rows.length > 0 && (
              <Badge variant="outline" className="text-[10px] ml-1">{rows.length}</Badge>
            )}
          </CardTitle>
          <div className="flex gap-1.5 flex-wrap">
            <TabBtn id="all" label="All" />
            {clientId && <TabBtn id="client" label="For this client" />}
            {(roleId || venueName) && <TabBtn id="role" label={`Same ${roleName || 'role'}/venue`} />}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-500 py-3" data-testid="previous-workers-empty">
            No previous worker history yet for this {tab === 'client' ? 'client' : 'role/venue'}.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100" data-testid="previous-workers-list">
            {rows.map((t) => {
              const ts = t.last_worked_at ? new Date(t.last_worked_at) : null;
              return (
                <li key={t.user_id} className="py-2.5 flex items-center gap-3" data-testid={`previous-worker-row-${t.user_id}`}>
                  <button
                    type="button"
                    onClick={() => openProfile(t.user_id)}
                    className="shrink-0 hover:ring-2 hover:ring-cyan-300 rounded-full transition"
                    data-testid={`previous-worker-photo-${t.user_id}`}
                  >
                    {t.profile_photo_url ? (
                      <img
                        src={t.profile_photo_url}
                        alt={t.display_name}
                        className="w-9 h-9 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                        {(t.display_name || '?').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </button>
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => openProfile(t.user_id)}
                      className="font-medium text-gray-900 text-sm truncate text-left hover:text-cyan-700 hover:underline block w-full"
                    >
                      {t.display_name}
                    </button>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                      {(t.city || t.state) && (
                        <span className="inline-flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" /> {[t.city, t.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {t.avg_rating > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-amber-600">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {Number(t.avg_rating).toFixed(1)}
                        </span>
                      )}
                      <span>· {t.gigs_count} gig{t.gigs_count === 1 ? '' : 's'}</span>
                      {ts && <span>· last {formatDate(ts)}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.worked_for_client && (
                        <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px]">For this client</Badge>
                      )}
                      {t.worked_role && (
                        <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[10px]">{roleName || 'Same role'}</Badge>
                      )}
                      {t.worked_venue && (
                        <Badge className="bg-violet-50 text-violet-700 border border-violet-200 text-[10px]">Same venue</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openInviteToShift(t.user_id, t.display_name)}
                      className="border-cyan-400 text-cyan-700 hover:bg-cyan-50 h-8"
                      data-testid={`previous-worker-invite-${t.user_id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" /> Invite
                    </Button>
                    <AddToInviteCartButton
                      talent={{
                        user_id: t.user_id,
                        name: t.display_name,
                        photo_url: t.profile_photo_url,
                        role: roleName,
                        city: t.city,
                        state: t.state,
                      }}
                      variant="icon"
                      testIdSuffix={`previous-${t.user_id}`}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default PreviousWorkersSection;
